// Plan gating for client controls (entitlements D45/D47, rbac-restore D53 / G4,
// simple-mode S4).
//
// GET /entitlements returns the active workspace's effective feature values, each with
// the server's own `remedy` text, plus credits, usage and the caller's account standing.
// A plan-gated control reads it here to DISABLE itself with that remedy; the gateway still
// denies with 403 `plan_restricted` + `X-Entitlement-Denied: <key>` (or 402
// `credits_exhausted` on a tool call), which `isEntitlementDenial` recognises. Every such
// denial re-reads entitlements exactly once, from the query client's caches
// (`createAppQueryClient`), so a control catches up without each view wiring it.
//
// Fail closed: while the read is loading, or if it fails (503), a gate is NOT allowed.
//
// THE one entitlements module (tranche-3 integration): one GET /entitlements query under
// qk.entitlements(workspaceId). ModeContext (Developer mode, `feature(key)`) and every
// plan-gated view read it through here; nothing else defines the query or the remedies.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import type { Entitlements } from '../api/types'
import { useSession } from '../context/SessionContext'
import { FALLBACK_REMEDY, isEntitlementDenial } from './denial'

// The denial predicates and the app QueryClient live in ./denial (no SessionContext
// import, so test utilities can build the client without pulling in the session).
export {
  FALLBACK_REMEDY,
  createAppQueryClient,
  isCreditsExhausted,
  isEntitlementDenial,
  isPlanRestricted,
  refreshOnEntitlementDenial,
} from './denial'

/** Where every upsell link points (Owner ruling pending, next-cycle-overview Q9). */
export const PLANS_PATH = '/plans'

/** The server's remedy for `key` from GET /entitlements, else the generic fallback. */
export function remedyFor(ent: Entitlements | undefined, key: string): string {
  const r = ent?.features?.[key]?.remedy
  return typeof r === 'string' && r.trim() ? r : FALLBACK_REMEDY
}

/** The plan's display name (or its key when it has none). */
export function planName(ent: Entitlements): string {
  return ent.plan.display_name || ent.plan.name
}

/** A boolean entitlement is on only when the gateway says exactly `true` (unknown ⇒ off). */
export function featureOn(ent: Entitlements | undefined, key: string): boolean {
  return ent?.features?.[key]?.value === true
}

export interface Gate {
  /** True only when the entitlement read succeeded and the plan allows the action. */
  allowed: boolean
  loading: boolean
  /** Why the control is disabled (empty when allowed). */
  reason: string
  /** Denied by the plan itself (not loading, not a failed read): offer the upsell. */
  upsell: boolean
}

export function useEntitlements() {
  const { workspaceId } = useSession()
  return useQuery({
    queryKey: qk.entitlements(workspaceId),
    queryFn: () => apiFetch<Entitlements>('/entitlements'),
    enabled: !!workspaceId,
  })
}

type EntQuery = ReturnType<typeof useEntitlements>

function gateFrom(q: EntQuery, decide: (e: Entitlements) => { allowed: boolean; reason: string }): Gate {
  if (q.data) {
    const d = decide(q.data)
    return { ...d, loading: false, upsell: !d.allowed }
  }
  if (q.isLoading) return { allowed: false, loading: true, reason: 'Checking your plan…', upsell: false }
  return { allowed: false, loading: false, reason: 'Couldn’t check your plan. Reload to try again.', upsell: false }
}

/** A boolean plan feature, e.g. `auth_configs.byo`. */
export function useFeature(key: string): Gate {
  const q = useEntitlements()
  return gateFrom(q, (e) => {
    const on = featureOn(e, key)
    return { allowed: on, reason: on ? '' : `Not included in your plan. ${remedyFor(e, key)}` }
  })
}

/**
 * Room for one more item under int limit `key` (null = unlimited), counted by
 * `usage[usageKey]` from the same response plus `extra` items the server also counts but
 * that usage key does not report.
 */
export function useCapacity(
  key: string,
  usageKey: keyof Entitlements['usage'],
  extra = 0,
): Gate & { limit?: number | null; used?: number } {
  const q = useEntitlements()
  const used = q.data ? (q.data.usage?.[usageKey] ?? 0) + extra : undefined
  const raw = q.data?.features?.[key]?.value
  const limit = raw === null ? null : typeof raw === 'number' ? raw : 0
  const gate = gateFrom(q, (e) => {
    const ok = limit === null || (used ?? 0) < limit
    return {
      allowed: ok,
      reason: ok ? '' : `Your plan allows up to ${limit}. ${remedyFor(e, key)}`,
    }
  })
  return q.data ? { ...gate, limit, used } : gate
}

/**
 * POST /workspaces pre-check from `account` (the caller's owned workspaces against the
 * `workspaces.max` the gateway enforces, computed by the same function as the gate).
 * Loading or a failed read: not allowed (fail closed). A response without `account`
 * (a gateway older than S4) leaves the decision to the server: allowed, and a 403 still
 * shows the server's remedy.
 */
export function useWorkspaceCreateGate(): Gate & { owned?: number; limit?: number | null } {
  const q = useEntitlements()
  return {
    ...gateFrom(q, (e) => {
      const acc = e.account as Entitlements['account'] | undefined
      if (!acc) return { allowed: true, reason: '' }
      const ok = acc.can_create_workspace === true
      return {
        allowed: ok,
        reason: ok ? '' : `Your plan allows up to ${acc.workspaces_max} workspace${acc.workspaces_max === 1 ? '' : 's'} you own. ${remedyFor(e, 'workspaces.max')}`,
      }
    }),
    owned: q.data?.account?.owned_workspaces,
    limit: q.data?.account?.workspaces_max,
  }
}

/** The one-time credits picture for a credits plan (Free); null on a monthly plan. */
export interface CreditsState {
  balance: number
  granted: number
  exhausted: boolean
  remedy: string
}

export function creditsState(ent: Entitlements | undefined): CreditsState | null {
  if (!ent) return null
  const kind = ent.features?.['calls.quota_kind']?.value
  if (kind !== 'credits' || ent.credits?.unused) return null
  const balance = Math.max(0, ent.credits?.balance ?? 0)
  return {
    balance,
    granted: ent.credits?.granted ?? 0,
    exhausted: balance <= 0,
    remedy: remedyFor(ent, 'calls.credits'),
  }
}

export function useCredits(): CreditsState | null {
  return creditsState(useEntitlements().data)
}

/**
 * The text for a plan or credits denial: the gateway's own detail and remedy (the
 * server's remedy wins; the generic fallback only when it sent none). Null when `err`
 * is neither.
 */
export function planDenialMessage(err: unknown): string | null {
  if (!isEntitlementDenial(err)) return null
  const detail = typeof err.detail === 'string' ? err.detail : ''
  const remedy = err.remedy || FALLBACK_REMEDY
  return [detail, remedy].filter(Boolean).join(' ')
}

/** Re-read entitlements (after a create/delete changes usage). */
export function useInvalidateEntitlements() {
  const qc = useQueryClient()
  const { workspaceId } = useSession()
  return () => void qc.invalidateQueries({ queryKey: qk.entitlements(workspaceId) })
}
